//region imports
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import TaskView from './TaskView.component.jsx';
import TasksActionButtons from './MyTasksButtonsBarView.component.jsx';
import TaskListHeader from './TaskListHeaderView.component.jsx';
import Search from './SearchView.component.jsx';
import '../pagelayout/global.scss';
import {ServerError} from '../errorpage/ErrorPage.component';
import translate from '../translations/translations.wrapper.jsx';
import {MyTasksActions} from './AvailableActions.class';
import {
    changeAllTaskSelection,
    changeCompanyFilter,
    changeSearchFilter,
    changeSort,
    changeTaskSelection,
    changeTasksSelection,
    loadTasks,
    saveScrollPosition,
    changeDisplayedDate,
    changeAutopayURL
} from './myTasks.action';

import {loadHistory} from '../myhistory/myHistory.action';
import {
    actionFinished,
    changeOneHandleOnlyFlag,
    taskAction,
    changeTryToLoopTasksFlag
} from './action/taskAction.action';
import * as reduxSelectors from '../store/application.reducers';
import {login} from "utils/api/odp.api";
import {LOADING_STATUS, MODAL_ACTIONS, MODAL_SIZE, TASK_ACTIONS} from "utils/constants";
import {useModalContext} from "components/modals/ModalContext";
import {keyPressHandler} from "utils/keyPressHandler.function";
import {snowplowTrackEvent} from "utils/snowplow";
import AsyncTaskActionExecutor from './AsyncTaskActionExecutor.component';
import TableFooter from "components/resizableTable/TableFooter.component.tsx";
import {Notification, NotificationType} from "components/common/Notification.component";
import NewPaymentBatch from "components/mytasks/NewPaymentBatch.component";
import { Loader, LoaderSize } from "components/common/Loader.component.tsx";
import { launchDarkly } from "utils/launchDarkly";
import TasksGrid from 'components/mytasks/TasksGrid.component';
import BetaSwitch from './BetaSwitch.component';
import BetaIntro from './BetaIntro.component';
import {GROUP_BY, groupWrappers, approvableKeys} from './tasksGrouping.function';
import {sumTotalAmount} from './taskAmounts.function';
import {
    readStoredBeta,
    writeStoredBeta,
    hasSeenBetaIntro,
    markBetaIntroSeen
} from './betaVersion.function';
import {
    COLUMN_FIELDS,
    fieldLabel,
    readStoredColumns,
    readStoredLayout,
    resolveVisibleFields,
    toggleField,
    writeStoredColumns,
    writeStoredLayout
} from './tasksGrid.function';


export class MyTasks extends Component {

    constructor(props) {
        super(props);
        this.sortTasks = this.sortTasks.bind(this);
        this.handleKeyPress = this.handleKeyPress.bind(this);
        this.toggleTask = this.toggleTask.bind(this);
        this.toggleGroup = this.toggleGroup.bind(this);
        this.approveGroup = this.approveGroup.bind(this);
        this.toggleGroupSelection = this.toggleGroupSelection.bind(this);
        this.toggleColumn = this.toggleColumn.bind(this);
        this.resetColumns = this.resetColumns.bind(this);
        this.changeColumnLayout = this.changeColumnLayout.bind(this);
        this.toggleBeta = this.toggleBeta.bind(this);
        this.changeGroupBy = this.changeGroupBy.bind(this);
        this.scrollContainer = React.createRef();
        this.list = React.createRef();

        const storedBeta = readStoredBeta();

        // local UI state for the flag-gated overview (grouping/columns)
        this.state = {
            groupBy: GROUP_BY.URGENCY,
            collapsedGroups: {},
            // null = show the default columns; an array = the user picked their own columns
            selectedColumns: readStoredColumns(),
            // null = the columns as they come; an object = the order and widths the user dragged them into
            columnLayout: readStoredLayout(),
            // opt-in: the beta stays off until the user turns it on
            betaEnabled: storedBeta === true
        };
    }

    /**
     * the Beta switch picks between the new overview and the old flat list
     */
    toggleBeta() {
        this.setState(state => ({betaEnabled: !state.betaEnabled}), () => {
            const betaEnabled = this.state.betaEnabled;
            writeStoredBeta(betaEnabled);

            // the note repeats on every switch-on until the user ticks "Do not show this again"
            if (betaEnabled && !hasSeenBetaIntro()) {
                this.showBetaIntro();
            }
        });
    }

    /**
     * usage of the beta overview, to see which of its controls people actually reach for
     */
    trackEvent(action, label) {
        snowplowTrackEvent({category: 'my_tasks', action, label}, this.props.userConfiguration);
    }

    changeGroupBy(groupBy) {
        this.setState({groupBy});
        this.trackEvent('group_by', groupBy);
    }

    /**
     * "beta version" note, on the shared confirm modal with only an acknowledge button
     */
    showBetaIntro() {
        const toggleModal = this.props.toggleModal;
        if (!toggleModal) return;

        let doNotShowAgain = false;

        toggleModal(MODAL_ACTIONS.CONFIRM_ACTION, {
            // the shared modal labels itself "Confirmation", which this one is not
            label: '',
            title: this.props.translate('myTasks.overview.introTitle'),
            size: MODAL_SIZE.SMALL,
            moreContent: <BetaIntro
                translate={this.props.translate}
                onDoNotShowAgainChange={(checked) => doNotShowAgain = checked}
            />,
            confirmButtonText: 'popUp.ok',
            hideClose: true,
            handleAction: () => {
                if (doNotShowAgain) markBetaIntroSeen();
                toggleModal(null);
            }
        });
    }

    toggleColumn(field) {
        const selectedColumns = toggleField(this.getVisibleColumns(), field);
        writeStoredColumns(selectedColumns);
        this.setState({selectedColumns});
    }

    resetColumns() {
        writeStoredColumns(null);
        writeStoredLayout(null);
        this.setState({selectedColumns: null, columnLayout: null});
    }

    /**
     * the grid reports the order and widths the user just dragged the columns into
     */
    changeColumnLayout(columnLayout) {
        writeStoredLayout(columnLayout);
        this.setState({columnLayout});
    }

    getVisibleColumns() {
        return resolveVisibleFields(this.state.selectedColumns);
    }

    /**
     * whether there is anything of the user's own to put back
     */
    isGridCustomised() {
        return this.state.selectedColumns !== null || this.state.columnLayout !== null;
    }

    /**
     * every column the picker can offer, flagged with whether it is currently shown
     */
    getColumnOptions() {
        const visible = new Set(this.getVisibleColumns());
        return COLUMN_FIELDS.map(field => ({
            field,
            label: fieldLabel(field, this.props.translate),
            isVisible: visible.has(field)
        }));
    }

    toggleGroup(key) {
        this.setState(prev => ({
            collapsedGroups: {...prev.collapsedGroups, [key]: !prev.collapsedGroups[key]}
        }));
    }

    approveGroup(wrappers) {
        const keys = approvableKeys(wrappers);
        if (keys.length > 0) {
            this.props.taskAction(TASK_ACTIONS.APPROVE, keys);
        }
    }

    componentDidMount() {

        //handle shortcut keys
        window.addEventListener('keydown', this.handleKeyPress);
        // start loading tasks
        this.props.loadTasks();
        // reset the flag, allowing only one handle
        this.props.changeOneHandleOnlyFlag(true);
        //my tasks actions should not try to loop the tasks
        this.props.changeTryToLoopTasksFlag(false);

        this.props.changeAutopayURL(null);

        // reset the dialog if opened - it holds data about task, but data might have expired (for example when user changes the context),
        // so we should close the dialog each time user returns to myTasks
        this.props.actionFinished();

        // which of the two versions the user actually works in, counted once per visit
        if (this.props.showBetaSwitch) {
            this.trackEvent('version_used', this.state.betaEnabled ? 'beta' : 'old');
        }

        if (this.props.scrollTop && this.scrollContainer.current && this.scrollContainer.current.scrollTop) {
            this.scrollContainer.current.scrollTop = this.props.scrollTop;
        }
    }

    sortTasks(event) {
        this.props.changeSort(event);
    }

    componentWillUnmount() {
        let scrollPosition = this.scrollContainer?.current?.scrollTop ? this.scrollContainer.current.scrollTop : 0;
        window.removeEventListener('keydown', this.handleKeyPress);
        this.props.saveScrollPosition(scrollPosition);
    }

    componentDidUpdate(prevProps) {
        if ((this.props.scrollTop || this.props.changeDisplayedDate) && this.list && this.scrollContainer && this.props.scrollTop > 0) {
            this.scrollContainer.current.scrollTop = this.props.scrollTop;
        }
    }

    handleKeyPress(e) {
        e.stopPropagation();
        let activeActions = new MyTasksActions(this.props.tasks.selectedTasks);
        keyPressHandler(e, this.props.taskAction, this.props.tasks.selectedKeys, activeActions);
    }

    toggleTask(event) {
        let scrollPosition = this.scrollList && this.scrollList.scrollTop ? this.scrollList.scrollTop : 0;
        this.props.saveScrollPosition(scrollPosition);
        this.props.changeTaskSelection(event);
    }

    /**
     * the group's own select all: selects every task of the group, or clears them once they all are
     */
    toggleGroupSelection(wrappers) {
        const allSelected = wrappers.every(wrapper => wrapper.selected);
        this.props.changeTasksSelection(wrappers.map(wrapper => wrapper.task.key), !allSelected);
    }

    getTasksAsComponent(wrappers = this.props.tasks.filteredTasks) {
        return wrappers.map(taskWrapper => {
            const task = taskWrapper.task;
            const taskProps = {
                task: task,
                translate: this.props.translate,
                selectCallback: this.toggleTask,
                selected: taskWrapper.selected,
                displayedDate: this.props.displayedDate,
                showLastChangedBy: this.props.showLastChangedBy
            };
            const taskActions = new MyTasksActions(task);
            // pass callback, only if actions are allowed
            if (taskActions.isEnabled(TASK_ACTIONS.APPROVE)) {
                taskProps.approveCallback = () => {
                    this.props.taskAction(TASK_ACTIONS.APPROVE, [task.key], {hasDraftComment: task.hasDraftComment});
                };
            }
            if (taskActions.isEnabled(TASK_ACTIONS.REJECT)) {
                taskProps.rejectCallback = () => {
                    this.props.taskAction(TASK_ACTIONS.REJECT, [task.key]);
                };
            }
            if (taskActions.isEnabled(TASK_ACTIONS.REVIEW)) {
                taskProps.reviewCallback = () => {
                    this.props.taskAction(TASK_ACTIONS.REVIEW, [task.key]);
                };
            }

            return (
                <TaskView
                    key={task.id}
                    {...taskProps}
                />);

        })
    }

    renderGrid() {
        const groups = groupWrappers(this.props.tasks.filteredTasks, this.state.groupBy, Date.now());
        if (groups.length === 0) {
            return <NoTasksFound translate={this.props.translate}/>;
        }
        return (
            <TasksGrid
                groups={groups}
                collapsedGroups={this.state.collapsedGroups}
                visibleFields={this.getVisibleColumns()}
                columnLayout={this.state.columnLayout}
                onColumnLayoutChange={this.changeColumnLayout}
                translate={this.props.translate}
                allSelected={this.props.tasks.areAllSelected}
                onToggleAll={() => this.props.changeAllTaskSelection()}
                onToggleTask={this.toggleTask}
                onToggleGroup={this.toggleGroup}
                onToggleGroupSelection={this.toggleGroupSelection}
                onApproveGroup={this.approveGroup}
                taskAction={this.props.taskAction}
            />
        );
    }

    render() {
        switch (this.props.loadingStatus) {

            case LOADING_STATUS.LOADING:
                return (
                    <div className="position-fixed top-50 w-100">
                        <Loader size={LoaderSize.Large} />
                    </div>
                );

            case LOADING_STATUS.ERROR:
                return (
                    <ServerError msg={this.props.tasks}/>
                );


            // braces so the declarations below stay scoped to this clause
            case LOADING_STATUS.DONE: {
                let activeActions = new MyTasksActions(this.props.tasks.selectedTasks);

                // flags
                const oneCompanyOnlyUser = this.props.availableContextsCount === 1;


                const overviewEnabled = this.props.showBetaSwitch && this.state.betaEnabled;

                // pinned to the bottom left of the page, in the footer strip
                const betaSwitch = this.props.showBetaSwitch &&
                    <BetaSwitch
                        isChecked={this.state.betaEnabled}
                        onChange={this.toggleBeta}
                        translate={this.props.translate}
                    />;

                let content;
                if (!overviewEnabled) {
                    const tasksAsComponents = this.getTasksAsComponent();
                    content = tasksAsComponents.length > 0 ? tasksAsComponents :
                        <NoTasksFound translate={this.props.translate}/>;
                }

                if (this.props.tasks.allTasks.length === 0 && this.props.tasksWithPayment.length === 0 && !this.props.batchTasks) {
                    // the switch stays reachable, so an empty beta view can still be left
                    return (
                        <>
                            {betaSwitch}
                            <NoTasks translate={this.props.translate}/>
                        </>
                    );
                } else {
                    let myTasksClass = this.props.hiddenTasks > 0 ? "my-tasks-content hidden-tasks-warning" : "my-tasks-content";
                    if (this.props.batchTasks) {
                        myTasksClass += " batch-tasks-area";
                    }
                    if (overviewEnabled) {
                        myTasksClass += " my-tasks-overview";
                    }

                    return (
                        <div
                            className={"ast_task_list " + ((this.props.hiddenTasks > 0 || this.props.batchTasks) ? "task_list__hidden" : "")}
                            ref="mytasks">
                            {this.props.hiddenTasks > 0  &&
                                <section><HiddenTasksWarning hiddenTasks={this.props.hiddenTasks}
                                                             translate={this.props.translate}/></section>
                            }
                            {this.props.batchTasks &&
                                <section className="batch-tasks mb-4">
                                    <NewPaymentBatch batch={this.props.batchTasks}
                                                     translate={this.props.translate}/>
                                </section>
                            }

                            <AsyncTaskActionExecutor translate={this.props.translate}/>
                            <Search updateSearchCallback={this.props.changeSearchFilter}
                                    updateCompanyFilterCallback={this.props.changeCompanyFilter}
                                    searchValue={this.props.search}
                                    onlyCurrentCompany={this.props.company}
                                    oneCompanyOnlyUser={oneCompanyOnlyUser}
                                    displayedDate={this.props.displayedDate}
                                    changeDisplayedDate={this.props.changeDisplayedDate}
                                    overviewEnabled={overviewEnabled}
                                    groupBy={this.state.groupBy}
                                    onGroupByChange={this.changeGroupBy}
                                    columns={overviewEnabled ? this.getColumnOptions() : null}
                                    onColumnToggle={this.toggleColumn}
                                    onColumnsReset={this.resetColumns}
                                    columnsCustomised={this.isGridCustomised()}
                            />
                            <main className={myTasksClass}>
                                {overviewEnabled ?
                                    // ag-grid renders its own column header and owns the scroll area
                                    this.renderGrid() :
                                    <ul className="list-unstyled">
                                        <li className="task-list-container no-padding margin-bottom app-width">
                                            <div className="task-header app-width">
                                                <TaskListHeader
                                                    sortByCallback={this.sortTasks}
                                                    selectAllCallback={this.props.changeAllTaskSelection}
                                                    currentSort={this.props.sort}
                                                    translate={this.props.translate}
                                                    currentAllSelected={this.props.tasks.areAllSelected}
                                                    displayedDate={this.props.displayedDate}
                                                    showLastChangedBy={this.props.showLastChangedBy}
                                                />
                                            </div>

                                            <div className="task-list-scroll-wrapper" ref={this.scrollContainer}>
                                                <ul className="task-list-content" aria-live="polite" ref={elem => {
                                                    this.list = elem && elem.getBoundingClientRect();
                                                }}>
                                                    {content}
                                                </ul>
                                            </div>
                                        </li>
                                    </ul>
                                }

                                <TableFooter/>
                                {betaSwitch}
                                <TasksActionButtons
                                    selectedTaskKeys={this.props.tasks.selectedKeys}
                                    userSettings={this.props.userConfiguration.userSettings}
                                    tasks={this.props.tasks.filteredTasks}
                                    action={this.props.taskAction}
                                    actions={activeActions}
                                    pendingTotals={overviewEnabled ? sumTotalAmount(this.props.tasks.filteredTasks) : null}
                                />

                            </main>
                        </div>
                    );
                }
            }

            default:
                // pass translate, otherwise it creates another connected component, which break the unit testing of myTasks.component
                return <ServerError translate={this.props.translate}/>;
        }

    }
}

MyTasks.propTypes = {
    userConfiguration: PropTypes.shape({
        userSettings: PropTypes.object,
    }),
    availableContextsCount: PropTypes.number,
    tasks: PropTypes.shape({
        allTasks: PropTypes.array,
        filteredTasks: PropTypes.array,
        selectedTasks: PropTypes.array,
        selectedKeys: PropTypes.array,
        areAllSelected: PropTypes.bool,
    }),
    loadingStatus: PropTypes.string,
    sort: PropTypes.object,
    search: PropTypes.string,
    company: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    scrollTop: PropTypes.number,
    hiddenTasks: PropTypes.number,
    displayedDate: PropTypes.string,
    batchTasks: PropTypes.object,
    tasksWithPayment: PropTypes.array,
    showLastChangedBy: PropTypes.bool,
    showBetaSwitch: PropTypes.bool,
    // from the modal context, missing when the page is rendered outside the layout
    toggleModal: PropTypes.func,
    translate: PropTypes.func.isRequired,
    changeTaskSelection: PropTypes.func.isRequired,
    changeTasksSelection: PropTypes.func.isRequired,
    changeAllTaskSelection: PropTypes.func.isRequired,
    changeSort: PropTypes.func.isRequired,
    changeSearchFilter: PropTypes.func.isRequired,
    changeCompanyFilter: PropTypes.func.isRequired,
    loadTasks: PropTypes.func.isRequired,
    changeOneHandleOnlyFlag: PropTypes.func.isRequired,
    changeTryToLoopTasksFlag: PropTypes.func.isRequired,
    actionFinished: PropTypes.func.isRequired,
    taskAction: PropTypes.func.isRequired,
    saveScrollPosition: PropTypes.func.isRequired,
    changeDisplayedDate: PropTypes.func.isRequired,
    changeAutopayURL: PropTypes.func.isRequired,
};

const withTranslations = translate(MyTasks);

const mapStateToProps = function (store) {
    return {
        userConfiguration: reduxSelectors.getUsersData(store),
        currentContextId: reduxSelectors.getUsersCurrentContextId(store),
        availableContextsCount: reduxSelectors.getAvailableContextsCount(store),
        tasks: reduxSelectors.getTasksForRender(store),
        loadingStatus: reduxSelectors.getLoadingStatus(store),
        sort: reduxSelectors.getSort(store),
        search: reduxSelectors.getFilterText(store),
        company: reduxSelectors.getFilterCompany(store),
        actionParameters: reduxSelectors.getActionParameters(store),
        scrollTop: reduxSelectors.getScrollPosition(store),
        hiddenTasks: reduxSelectors.getHiddenTasks(store),
        displayedDate: reduxSelectors.getDisplayedDate(store),
        batchTasks: reduxSelectors.getActiveBatch(store),
        tasksWithPayment: reduxSelectors.getTasksWithPayment(store),
        showLastChangedBy: reduxSelectors.getShowLastChangedBy(store)
    };
};


const Connected = connect(mapStateToProps, {
    changeTaskSelection,
    changeTasksSelection,
    changeAllTaskSelection,
    changeSort,
    changeSearchFilter,
    changeCompanyFilter,
    loadTasks,
    changeOneHandleOnlyFlag,
    changeTryToLoopTasksFlag,
    actionFinished,
    taskAction,
    loadHistory,
    saveScrollPosition,
    changeDisplayedDate,
    changeAutopayURL
})(withTranslations);

// Per-tenant flag offering the redesigned overview (grouping + column picker)
// next to the classic list, picked with the "Try the Beta version" switch; default false
// keeps the current flat list as production behavior.
const MyTasksWithFlag = (props) => {
    const showBetaSwitch = launchDarkly.companyClient?.variation("show-my-tasks-beta-switch", false);
    const {toggleModal} = useModalContext() || {};
    return <Connected {...props} showBetaSwitch={showBetaSwitch} toggleModal={toggleModal}/>;
};

export default MyTasksWithFlag;


const NoTasks = (props) => {
    return (
        <div className="no-tasks-message text-center ast_task_list">
            <span className="col-md-12 icon icon-48 smiley-face"/>
            <span
                className="col-md-10 text-left margin-bottom"><b>{props.translate("myTasks.allTasksHandled")}</b></span>
            <br/>
            <span className="col-md-10 text-left">{props.translate("myTasks.notifyYou")}</span>
        </div>)
};

NoTasks.propTypes = {
    translate: PropTypes.func.isRequired,
};

const NoTasksFound = (props) => {
    return (
        <div className="no-tasks-found text-disabled">
            {props.translate('myTasks.noTasksFound')}
        </div>
    )
};

NoTasksFound.propTypes = {
    translate: PropTypes.func.isRequired,
};

const HiddenTasksWarning = (props) => {
    let message = props.hiddenTasks > 1 ? "myTasks.hiddenTasksWarning" : "myTasks.hiddenTaskWarning";
    let licenseMessage = props.hiddenTasks > 1 ? "myTasks.loginLinkPlural" : "myTasks.loginLinkSingular";
    return (
        <div className="d-flex justify-content-center my-3">
            <Notification type={NotificationType.INFORMATION}
                          description={
                              <div>
                                  {props.translate(message, props.hiddenTasks)}
                                  <a href="#" onClick={login}>{props.translate(licenseMessage)}</a>
                                  {props.translate("myTasks.licenseWarning")}
                              </div>
                          }/>
        </div>
    )
};

HiddenTasksWarning.propTypes = {
    hiddenTasks: PropTypes.number.isRequired,
    translate: PropTypes.func.isRequired,
};